import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import { useIntl } from "react-intl";

import HasSourceObject from "@mapmycustomers/shared/types/base/HasSourceObject";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import FormLayout, { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { LoadingSpinner } from "@mapmycustomers/ui";

import Footer from "@app/component/createEditEntity/components/Footer";
import DuplicatesTable from "@app/component/DuplicatesTable";
import convertFieldToLayoutSchemaField from "@app/component/FormFields/utils/convertFieldToLayoutSchemaField";
import FormLayoutSelector from "@app/component/FormLayoutSelector";
import FormNoLayoutAlert from "@app/component/FormNoLayoutAlert";
import EntityModal from "@app/component/modal/EntityModal";
import { showGlobalPostCreationModal } from "@app/component/PostCreationModal/store/actions";
import PreviewModeIndicator from "@app/component/PreviewModeIndicator";
import { CreatePersonPayload } from "@app/store/api/ApiService";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import useFormChangeTracker from "@app/util/hook/form/useFormChangeTracker";
import useDefaultSourceObjectKey from "@app/util/hook/useDefaultSourceObjectKey";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import useFieldValidation from "@app/util/hook/useFieldValidation";
import useSourceObjectMessageArgs from "@app/util/hook/useSourceObjectMessageArgs";
import { personLayoutModel } from "@app/util/layout/impl";

import { personAnalytics } from "../analytics";
import GeocodeLimitField from "../components/GeocodeLimitField";
import { getAddress } from "../util/getAddress";

import PersonFormFields from "./PersonFormFields";
import { getUploadedFilesList, isCreating, isInitializing } from "./store";
import {
  clearAllUploadedPersonFiles,
  clearDuplicates,
  createPerson,
  initialize,
} from "./store/actions";
import { getDuplicates, isGlobalAddPerson } from "./store/selectors";
import FormValues from "./types/FormValues";
import getInitialValues from "./util/getInitialValues";

export interface CreatePersonModalProps {
  className?: string;
  fixedCompany?: Company;
  fixedEmail?: boolean;
  ignoreDuplicates?: boolean;
  initialValues?: Partial<Person>;
  isFieldDisabled?: (field: IField) => boolean;
  okText?: ReactNode;
  onHide: (newPerson?: Person) => void;
  previewLayout?: FormLayout;
  previewSourceObject?: HasSourceObject["sourceObject"];
  previewWarning?: ReactNode;
  renderFooter?: (args: {
    creating: boolean;
    hasDuplicates: boolean;
    isPreviewMode: boolean;
    onCancel: () => void;
    onCreate: (checkForDuplicates: boolean) => void;
    submitButtonDisabled: boolean;
  }) => ReactNode;
  showNotificationViewButton?: boolean;
  showPostCreationModal?: boolean;
}

const CreatePersonModal = ({
  className,
  clearAllUploadedFiles,
  createPerson,
  creating,
  currentUserId,
  duplicates,
  fixedCompany,
  fixedEmail,
  ignoreDuplicates,
  initialize,
  initializing,
  initialValues,
  isFieldDisabled,
  okText,
  onClearDuplicates,
  onHide,
  onShowGlobalPostCreationModal,
  previewLayout,
  previewSourceObject,
  previewWarning,
  renderFooter,
  showNotificationViewButton = true,
  showPostCreationModal,
  uploadedFiles,
}: CreatePersonModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const [formLayout, setFormLayout] = useState(personLayoutModel.defaultFormLayout);
  // A pre-linked company never passes through the association picker, so its auto-primary never fires
  const [primaryCompanyId, setPrimaryCompanyId] = useState<Company["id"] | undefined>(
    fixedCompany?.id
  );

  const [form] = useForm<FormValues>();
  const [isFormChanged, handleFormChanges] = useFormChangeTracker(form);
  const [formContainer, handleFinishFail] = useErroredFieldsScroller();

  useEffect(() => {
    initialize({ fixedCompany });
  }, [fixedCompany, initialize]);

  const handleVisibility = useCallback(
    (person: Person) => {
      onHide(person);
      if (showPostCreationModal) {
        onShowGlobalPostCreationModal({ entity: person, entityType: EntityType.PERSON });
      }
    },
    [showPostCreationModal, onHide, onShowGlobalPostCreationModal]
  );

  const handleFieldValidationErrors = useFieldValidation(form, handleFinishFail, true);

  const handleCancelClick = useCallback(() => {
    clearAllUploadedFiles();
    onHide();
  }, [clearAllUploadedFiles, onHide]);

  const handleCreate = useCallback(
    (checkDuplicates: boolean) => {
      const layoutId = formLayout?.id;

      if (layoutId) {
        const values = form.getFieldsValue();
        const address = getAddress(values, initialValues?.geoManagementState);

        const firstName = values?.firstName ?? "";
        const lastName = values?.lastName ?? "";

        const person: CreatePersonPayload = {
          ...address,
          accounts: values.accounts ?? [],
          cadenceInterval: values.cadenceInterval,
          color: values.color,
          crmActivityTypeId: values.crmActivityTypeId,
          email: values.email,
          firstName,
          geoAddress: initialValues?.geoAddress ?? address.geoAddress,
          geoPoint: initialValues?.geoPoint ?? address.geoPoint,
          lastName,
          name: `${firstName} ${lastName}`,
          phone: values.phone,
          primaryAccount: primaryCompanyId ? { id: primaryCompanyId } : null,
          user: values.user ? { id: values.user } : undefined,
        };

        createPerson({
          callback: handleVisibility,
          checkDuplicates: ignoreDuplicates ? false : checkDuplicates,
          customFieldsValues: values.customFields
            ? Object.values(values.customFields).filter(isDefined)
            : [],
          fieldsValueValidateCallback: handleFieldValidationErrors,
          groupsIdsToAdd: values.groups ? Array.from(values.groups) : [],
          layoutId,
          person,
          showNotificationViewButton,
        });
      }
    },
    [
      createPerson,
      form,
      formLayout?.id,
      handleFieldValidationErrors,
      handleVisibility,
      ignoreDuplicates,
      initialValues?.geoAddress,
      initialValues?.geoManagementState,
      initialValues?.geoPoint,
      primaryCompanyId,
      showNotificationViewButton,
    ]
  );

  const [parentCompanies, setParentCompanies] = useState<Company[]>(
    fixedCompany ? [fixedCompany] : []
  );

  const defaultSourceObjectKey = useDefaultSourceObjectKey(EntityType.PERSON);
  const isPreviewMode = !!previewLayout;
  // undefined means "not previewing", so the org default applies; null means "preview the
  // standard object". That's why not using ??.
  const sourceObject = isDefined(previewSourceObject)
    ? previewSourceObject
    : defaultSourceObjectKey;
  const titleMessageArgs = useSourceObjectMessageArgs(EntityType.PERSON, sourceObject);
  const currentLayout = isPreviewMode ? previewLayout : formLayout;

  const submitButtonDisabled = creating || initializing || isPreviewMode || !currentLayout;
  const showConfirm =
    !isPreviewMode && !initializing && (isFormChanged || uploadedFiles.length > 0);
  const hasDuplicates = duplicates.length > 0;

  const handleCreateClick = useCallback(
    (checkForDuplicates: boolean) => {
      if (checkForDuplicates) {
        form.submit();
      } else {
        handleCreate(false);
      }
    },
    [form, handleCreate]
  );

  const handleFormFinish = useCallback(() => {
    handleCreate(true);
  }, [handleCreate]);

  const defaultAddedFields = useMemo(() => {
    const fieldModel = getFieldModelByEntityType(EntityType.PERSON);
    const fieldNamesToAdd: PersonFieldName[] = [];

    if (fixedCompany) {
      fieldNamesToAdd.push(PersonFieldName.COMPANIES);
    }

    if (fixedEmail) {
      fieldNamesToAdd.push(PersonFieldName.EMAIL);
    }

    return fieldNamesToAdd.reduce((schemaField: LayoutSchemaField[], name) => {
      const field = fieldModel.getByName(name);
      if (field) {
        schemaField.push(convertFieldToLayoutSchemaField(field));
      }
      return schemaField;
    }, []);
  }, [fixedCompany, fixedEmail]);

  return (
    <AnalyticsContext.Provider value={personAnalytics}>
      <EntityModal
        className={className}
        entityTypeName="Person"
        footer={
          renderFooter ? (
            renderFooter({
              creating,
              hasDuplicates,
              isPreviewMode,
              onCancel: handleCancelClick,
              onCreate: handleCreateClick,
              submitButtonDisabled,
            })
          ) : (
            <Footer
              creating={creating}
              entityType={EntityType.PERSON}
              hasDuplicates={hasDuplicates}
              isPreviewMode={isPreviewMode}
              okDisabled={submitButtonDisabled}
              okText={okText}
              onCancel={handleCancelClick}
              onCreate={handleCreateClick}
              onGoBack={onClearDuplicates}
            />
          )
        }
        onCancel={handleCancelClick}
        showConfirm={showConfirm}
        title={
          hasDuplicates ? null : (
            <>
              {intl.formatMessage(
                {
                  id: "createPersonModal.title",
                  defaultMessage:
                    "{sourceObject, select, standard {Create Person} partnerPeople {Create Partner} leadPeople {Create Lead} staffMemberPeople {Create Staff Member} leadPersonnel {Create Lead Personnel} other {Create {label}}}",
                  description: "Title of Create Person modal, named after the source object",
                },
                titleMessageArgs
              )}

              {isPreviewMode ? (
                <PreviewModeIndicator />
              ) : (
                <FormLayoutSelector
                  disabled={creating || initializing}
                  entityType={EntityType.PERSON}
                  layout={formLayout}
                  onChange={setFormLayout}
                />
              )}
            </>
          )
        }
      >
        {initializing ? (
          <LoadingSpinner />
        ) : currentLayout ? (
          <>
            {hasDuplicates ? (
              <DuplicatesTable duplicates={duplicates} entityType={EntityType.PERSON} showAsLinks />
            ) : null}

            <div hidden={!!duplicates.length} ref={formContainer}>
              <GeocodeLimitField />

              <Form
                form={form}
                initialValues={getInitialValues(
                  currentUserId,
                  sourceObject,
                  initialValues,
                  fixedCompany
                )}
                layout="vertical"
                onFinish={handleFormFinish}
                onFinishFailed={handleFinishFail}
                onValuesChange={handleFormChanges}
                preserve
              >
                <PersonFormFields
                  defaultAddedFields={defaultAddedFields}
                  isFieldDisabled={isFieldDisabled}
                  layout={currentLayout}
                  parentCompanies={parentCompanies}
                  previewWarning={previewWarning}
                  primaryCompanyId={primaryCompanyId}
                  setParentCompanies={setParentCompanies}
                  setPrimaryCompanyId={setPrimaryCompanyId}
                />
              </Form>
            </div>
          </>
        ) : (
          <FormNoLayoutAlert />
        )}
      </EntityModal>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState, { showPostCreationModal }: CreatePersonModalProps) => ({
  creating: isCreating(state),
  currentUserId: getCurrentUserId(state)!,
  duplicates: getDuplicates(state),
  initializing: isInitializing(state),
  showPostCreationModal: showPostCreationModal ?? isGlobalAddPerson(state),
  uploadedFiles: getUploadedFilesList(state),
});

const mapDispatchToProps = {
  clearAllUploadedFiles: clearAllUploadedPersonFiles,
  createPerson: createPerson.request,
  initialize: initialize.request,
  onClearDuplicates: clearDuplicates,
  onShowGlobalPostCreationModal: showGlobalPostCreationModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreatePersonModal);
